import { useCallback, useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
const KEY = 'medicail.theme';

function readPref(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

const listeners = new Set<() => void>();
let current: ThemePreference = readPref();

function systemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  return pref === 'system' ? (systemDark() ? 'dark' : 'light') : pref;
}

function apply(pref: ThemePreference) {
  const dark = resolveTheme(pref) === 'dark';
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

export function setThemePreference(pref: ThemePreference): void {
  current = pref;
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* ignore */
  }
  apply(pref);
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Call once at boot to apply the saved theme and follow OS changes while on "system". */
export function initTheme(): void {
  apply(current);
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (current === 'system') {
      apply('system');
      listeners.forEach((l) => l());
    }
  });
}

export function useTheme() {
  const snap = useSyncExternalStore(
    subscribe,
    () => `${current}|${resolveTheme(current)}`,
    () => 'system|light',
  );
  const [preference, resolved] = snap.split('|') as [ThemePreference, 'light' | 'dark'];
  const setPreference = useCallback((p: ThemePreference) => setThemePreference(p), []);
  return { preference, resolved, setPreference };
}
